import { useEffect, useState } from 'react';
import useClient from 'api/client';
import { useAlert } from 'components/message/AlertContext';
import queryDatasetDetails from 'api/Dataset/queryDatasetDetails';
import { FormatDate } from 'components/dates/FormatDate';
import { useParams } from 'react-router-dom';
import { ItemsDetails } from 'components/details/EditorDetails';
import { Dataset, datasetDefault } from 'types/Dataset';

export default function useDatasetDetails() {
    const client = useClient();
    const [dataset, setDataset] = useState<Dataset>(datasetDefault);

    const { openAlert } = useAlert();
    const params = useParams<{ uri: string }>();

    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState<ItemsDetails>({
        created: '',
        label: '',
        description: '',
        data: [],
    });
    const [error, setError] = useState(false);
    const fetchDetails = async () => {
        try {
            setLoading(true);
            if (params.uri) {
                const response = await client?.query(
                    queryDatasetDetails(params.uri),
                );
                if (!response.errors) {
                    setDataset({
                        ...response.data.getDataset,
                        created: FormatDate(response.data.getDataset.created),
                    });

                    setItems({
                        label: response.data.getDataset.label,
                        description: response.data.getDataset.description,
                        data: [
                            {
                                label: 'Created by',
                                value: response.data.getDataset.owner,
                            },
                            {
                                label: 'Dataset URI',
                                value: response.data.getDataset.datasetUri,
                            },
                            {
                                label: 'S3BucketName',
                                value: response.data.getDataset.S3BucketName,
                            },
                            {
                                label: 'Business Owner',
                                value: response.data.getDataset
                                    .businessOwnerEmail,
                            },
                            {
                                label: 'Admin group',
                                value:
                                    response.data.getDataset
                                        .originSamlAdminGroupName ||
                                    response.data.getDataset.SamlAdminGroupName, // fallback to regular samlAdminGroup
                            },
                            {
                                label: 'Stewards',
                                value: response.data.getDataset
                                    .businessOwnerDelegationEmails,
                            },
                            {
                                label: 'Organization',
                                value: response.data.getDataset.organization
                                    .label,
                                uri: `/organization/${response.data.getDataset.organization.organizationUri}`,
                            },
                            {
                                label: 'Environment',
                                value: response.data.getDataset.environment
                                    .label,
                                uri: `/environment/${response.data.getDataset.environment.environmentUri}`,
                            },
                            {
                                label: 'Dataset Stages',
                                value: response.data.getDataset.datasetStage,
                            },
                            {
                                label: 'Crawler',
                                value: response.data.getDataset.enableCrawler
                                    ? 'Enabled'
                                    : 'Disabled',
                            },
                            {
                                label: 'Read Logs Tracking',
                                value: response.data.getDataset.enableReadTrail
                                    ? 'Enabled'
                                    : 'Disabled',
                            },
                        ],
                    });
                } else {
                    setError(true);
                    openAlert({
                        message: `Something went wrong... Could not retrieve dataset details:${response.errors[0]?.message}`,
                        severity: 'alert',
                    });
                }
            }
        } catch (error) {
            setError(true);
            openAlert({
                message: 'An error occurred while fetching dataset details',
                severity: 'alert',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (client) {
            fetchDetails();
        }
    }, [client, params.uri]);

    return { items, dataset, error, loading, fetchDetails };
}
